<template>
    <div
        class="date-filter"
        :data-test-id="`${field}-filter-field`"
    >
        <PrimeSelect
            class="date-filter__mode"
            :modelValue="mode"
            optionLabel="label"
            :options="modeOptions"
            optionValue="value"
            :pt="selectPassThrough"
            @update:modelValue="handleModeChange"
        >
            <template #option="{ option }">
                <span
                    class="date-filter__option"
                    :data-test-id="`${field}-filter-mode-option-${option.value}`"
                >
                    <span :class="{ 'date-filter__option-label--applied': isApplied(option.value) }">
                        {{ option.label }}
                    </span>
                    <i
                        v-if="isApplied(option.value)"
                        class="pi pi-check date-filter__check"
                        :data-test-id="`${field}-filter-mode-applied-${option.value}`"
                    />
                </span>
            </template>
        </PrimeSelect>
        <DatePicker
            :key="mode"
            class="date-filter__calendar"
            dateFormat="mm-dd-yy"
            inline
            :modelValue="selectedDate"
            :pt="datePickerPassThrough"
            :selectionMode="isRange ? 'range' : 'single'"
            @update:modelValue="handleDateChange"
        />
        <small
            v-if="isIncompleteRange"
            class="date-filter__message"
            :data-test-id="`${field}-filter-message`"
        >
            Select an end date to filter.
        </small>
    </div>
</template>

<script>
import DatePicker from 'primevue/datepicker'
import PrimeSelect from 'primevue/select'

// Every mode names the operator the backend expects, BETWEEN included.
const MODE_ON = 'on'
const MODE_BEFORE = 'before'
const MODE_AFTER = 'after'
const MODE_BETWEEN = 'between'
const MODES = [MODE_ON, MODE_BEFORE, MODE_AFTER, MODE_BETWEEN]
const MODE_LABELS = {
    [MODE_ON]: 'on…',
    [MODE_BEFORE]: 'before…',
    [MODE_AFTER]: 'after…',
    [MODE_BETWEEN]: 'between…',
}
// A BETWEEN filter only bounds a search once both of its dates are
// picked, and the calendar leaves the end null after the first one, so a
// half picked range is not something this filter can be applied with.
function isIncompleteDateRange(filter) {
    if (filter?.mode !== MODE_BETWEEN || !Array.isArray(filter.value)) {
        return false
    }

    return filter.value.filter(Boolean).length !== 2
}

export default {
    name: 'DateFilter',

    components: {
        DatePicker,
        PrimeSelect,
    },

    props: {
        field: {
            type: String,
            required: true,
        },
        label: {
            type: String,
            required: true,
        },
        // A `{ mode, value }` object, where mode is the operator the dates
        // were picked under and value is a Date, or a [start, end] pair of
        // them for BETWEEN. Null means the filter is untouched, which is
        // also what the filter menu's Clear button leaves behind.
        modelValue: {
            type: Object,
            required: false,
            default: null,
        },
    },

    // `update:valid` tells the filter menu whether what is collected so far
    // can be applied, which is how the Apply button knows to disable.
    emits: ['update:modelValue', 'update:valid'],

    data() {
        return {
            // The mode picked from the select, which only decides which
            // dates the calendar is collecting. Null until the user picks
            // one, so that the visible mode can follow the filter instead.
            chosenMode: null,
        }
    },

    computed: {
        // The mode the filter is applied under, which is the one that gets
        // the checkmark. A filter holds the dates of a single mode, so
        // there is never more than one.
        appliedMode() {
            return this.hasDate(this.modelValue?.value) ? this.modelValue.mode : null
        },
        // Reopening the menu should land on the mode that is filtering, so
        // an untouched select follows the applied filter.
        mode() {
            return this.chosenMode ?? this.appliedMode ?? MODE_ON
        },
        isRange() {
            return this.mode === MODE_BETWEEN
        },
        // Reads off the dates the calendar is showing rather than off the
        // filter, so switching away from a half picked BETWEEN takes the
        // message with it.
        isIncompleteRange() {
            return isIncompleteDateRange({ mode: this.mode, value: this.selectedDate })
        },
        modeOptions() {
            return MODES.map((mode) => ({
                label: `${this.label} ${MODE_LABELS[mode]}`,
                value: mode,
            }))
        },
        // Only the applied mode has dates to show, so switching to another
        // one opens an empty calendar.
        selectedDate() {
            return this.mode === this.appliedMode ? this.modelValue.value : null
        },
        selectPassThrough() {
            return {
                root: { 'data-test-id': `${this.field}-filter-mode-select` },
            }
        },
        datePickerPassThrough() {
            return {
                root: { 'data-test-id': `${this.field}-filter-date-picker` },
                day: ({ context }) => ({ 'data-test-id': this.dayTestId(context.date) }),
            }
        },
    },

    watch: {
        // The filter menu's Clear button empties the model, which puts the
        // select back to where an untouched filter starts.
        modelValue(value) {
            if (!value) {
                this.chosenMode = null
            }
        },
        // Reported on mount as well as on change, so that a menu reopened on
        // a filter that was left half picked still knows not to apply it.
        isIncompleteRange: {
            handler(incomplete) {
                this.$emit('update:valid', !incomplete)
            },
            immediate: true,
        },
    },

    methods: {
        isApplied(mode) {
            return mode === this.appliedMode
        },
        handleModeChange(mode) {
            this.chosenMode = mode
        },
        // The modes answer different questions, so only one of them filters
        // at a time: picking dates under one drops whatever another had
        // collected. BETWEEN's value is the range the picker hands over,
        // whose end stays null until the second date is picked.
        handleDateChange(value) {
            this.$emit('update:modelValue', this.hasDate(value) ? { mode: this.mode, value } : null)
        },
        hasDate(value) {
            return Array.isArray(value) ? value.some(Boolean) : Boolean(value)
        },
        // A stable id per day cell, so a date can be picked without
        // relying on the calendar's layout. PrimeVue hands back a zero
        // indexed month.
        dayTestId(date) {
            const month = `${date.month + 1}`.padStart(2, '0')
            const day = `${date.day}`.padStart(2, '0')

            return `${this.field}-filter-day-${date.year}-${month}-${day}`
        },
    },
}
</script>

<style lang="scss" scoped>
.date-filter {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 18em;
}

.date-filter__mode {
    width: 100%;
}

.date-filter__option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
}

.date-filter__option-label--applied {
    font-weight: 700;
}

.date-filter__message {
    color: #f87171;
}
</style>
